import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'
import { useFormattingContext } from '../organisms/FormattingProvider/FormattingContext'

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: '400px',
    overflow: 'auto',
    margin: '16px 0',
    border: '1px solid var(--color-neutral-400)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--dai-color-surface4)'
  },
  table: {
    minWidth: 500,
    borderCollapse: 'separate',
    borderSpacing: 0,
    backgroundColor: 'transparent'
  },
  tableRow: {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'var(--dai-color-surface3)'
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'var(--dai-color-surface4)'
    },
    '&:nth-of-type(even):hover': {
      backgroundColor: 'var(--dai-color-surface3)'
    }
  },
  tableCell: {
    fontSize: 'var(--font-size-sm)',
    padding: '10px 16px',
    color: 'var(--dai-color-text)',
    borderBottom: '1px solid var(--color-neutral-200)',
    '&:first-child': {
      paddingLeft: '20px'
    },
    '&:last-child': {
      paddingRight: '20px'
    }
  },
  headerRow: {
    backgroundColor: 'var(--dai-color-input)',
    '&:hover': {
      backgroundColor: 'var(--dai-color-input)'
    }
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: 'var(--dai-color-input)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    color: 'var(--color-primary-700)',
    borderBottom: '2px solid var(--color-neutral-300)',
    fontSize: 'var(--font-size-sm)',
    padding: '12px 16px',
    '&:first-child': {
      paddingLeft: '20px'
    },
    '&:last-child': {
      paddingRight: '20px'
    }
  },
  truncationNotice: {
    padding: '10px 20px',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--dai-color-text-secondary)',
    borderTop: '1px solid var(--color-neutral-300)',
    backgroundColor: 'var(--dai-color-surface4)',
    fontStyle: 'italic'
  }
}))

/**
 * InlineTable - A table component for embedding in markdown/messages
 *
 * Unlike other table components which require complex configurations or data providers,
 * InlineTable accepts a simple array of objects and column definitions. This is ideal
 * for AI-generated visualizations or when you need to display tabular data inline.
 *
 * Columns can specify a 'format' or 'formatString' property to use the FormattingContext formatter.
 * Format strings can be:
 * - Configuration aliases: 'currency', 'money', 'percentage', 'date', 'timestamp', etc.
 * - Numeral.js format strings: '0,0', '(0,0)', '0.0%', etc.
 * - Date format strings: '@YYYY-MM-DD' (UTC) or '&YYYY-MM-DD' (local)
 * - Relative time: 'ß' (from now)
 *
 * You can also pass a 'formattingConfiguration' prop to override the default configuration.
 *
 * @example
 * <InlineTable
 *   data={[
 *     { name: 'John', age: 30, salary: 50000, joinDate: '2020-01-15' },
 *     { name: 'Jane', age: 25, salary: 60000, joinDate: '2019-06-20' }
 *   ]}
 *   columns={[
 *     { key: 'name', label: 'Name' },
 *     { key: 'age', label: 'Age', format: 'number' },
 *     { key: 'salary', label: 'Salary', format: 'currency' },
 *     { key: 'joinDate', label: 'Join Date', format: 'date' }
 *   ]}
 *   maxHeight="300px"
 * />
 */
function InlineTable ({ data, maxHeight, columns, totalRows, formattingConfiguration = null }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext(formattingConfiguration)

  if (!Array.isArray(data) || data.length === 0) {
    return null
  }

  if (!Array.isArray(columns) || columns.length === 0) {
    return null
  }

  const isTruncated = totalRows != null && totalRows > data.length

  return (
    <TableContainer className={classes.tableContainer} style={{ maxHeight }}>
      <Table className={classes.table} size='small' stickyHeader>
        <TableHead>
          <TableRow className={classes.headerRow}>
            {columns.map((col) => (
              <TableCell key={col.key} className={classes.headerCell}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index} className={classes.tableRow}>
              {columns.map((col) => {
                const value = row[col.key]
                const formatString = col.format || col.formatString

                let displayValue
                if (formatString) {
                  displayValue = formatter(value, formatString)
                } else if (typeof value === 'object' && value !== null) {
                  displayValue = JSON.stringify(value)
                } else {
                  displayValue = String(value ?? '')
                }

                return (
                  <TableCell key={col.key} className={classes.tableCell}>
                    {displayValue}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isTruncated && (
        <div className={classes.truncationNotice}>
          Showing {data.length.toLocaleString()} of {totalRows.toLocaleString()} rows
        </div>
      )}
    </TableContainer>
  )
}

InlineTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  maxHeight: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    format: PropTypes.string,
    formatString: PropTypes.string
  })).isRequired,
  totalRows: PropTypes.number,
  formattingConfiguration: PropTypes.object
}

export default InlineTable
